import React, { useState, useEffect } from 'react';
import {
  Box,
  Modal,
  ModalDialog,
  Typography,
  Button,
  Table,
  Input,
  FormControl,
  FormLabel,
  Alert,
  Card,
  Select,
  Option
} from '@mui/joy';
import axiosInstance from '../util/axiosConfig';

const today = () => new Date().toISOString().slice(0, 10);

const MaterialPopup = ({ materialName, onClose }) => {
  const [batches, setBatches] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [consumptionModalBatch, setConsumptionModalBatch] = useState(null);
  const [consumptionForm, setConsumptionForm] = useState({ qty: '', date: today() });
  const [consumingBatchId, setConsumingBatchId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showAddStock, setShowAddStock] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [addStockForm, setAddStockForm] = useState({
    purchase_date: today(),
    quantity: '',
    rate: '',
    manufacture_date: '',
    expiry_date: '',
    vendor_id: ''
  });
  const [addStockSubmitting, setAddStockSubmitting] = useState(false);

  useEffect(() => {
    fetchBatches();
  }, [materialName]);

  useEffect(() => {
    if (showAddStock && vendors.length === 0) {
      fetchVendors();
    }
  }, [showAddStock]);

  const fetchVendors = async () => {
    try {
      setVendorsLoading(true);
      const response = await axiosInstance.get('/vendors');
      setVendors(response.data.vendors || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load vendors');
    } finally {
      setVendorsLoading(false);
    }
  };

  const fetchBatches = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axiosInstance.get(`/materials/batches/${encodeURIComponent(materialName)}`);
      const fetchedBatches = response.data.batches || [];
      setBatches(fetchedBatches);

      if (fetchedBatches.length > 0) {
        fetchHistory(fetchedBatches);
      } else {
        setHistory([]);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch batches');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async (batchesToUse) => {
    try {
      // Fetch history for all batches
      const historyPromises = batchesToUse.map(batch =>
        axiosInstance.get(`/materials/${batch.id}/history`)
      );
      const responses = await Promise.all(historyPromises);
      const allHistory = responses.flatMap(res => res.data.history || []);
      // Sort by date descending, then by id descending (latest first when date is same)
      allHistory.sort((a, b) => {
        const byDate = new Date(b.date) - new Date(a.date);
        if (byDate !== 0) return byDate;
        return (b.id - a.id);
      });
      setHistory(allHistory);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  };

  const openConsumptionModal = (batch) => {
    setConsumptionModalBatch(batch);
    setConsumptionForm({ qty: '', date: today() });
    setError('');
  };

  const handleConsumptionSubmit = async (e) => {
    e.preventDefault();
    if (!consumptionModalBatch) return;
    const num = parseInt(consumptionForm.qty, 10);
    if (!num || num <= 0) {
      setError('Enter a valid quantity');
      return;
    }
    if (num > consumptionModalBatch.quantity) {
      setError('Quantity cannot exceed current stock');
      return;
    }
    const chosenDate = consumptionForm.date || today();
    if (chosenDate > today()) {
      setError('Date cannot be in the future');
      return;
    }
    try {
      setConsumingBatchId(consumptionModalBatch.id);
      setError('');
      await axiosInstance.put(`/materials/${consumptionModalBatch.id}/quantity`, {
        changeAmount: -Math.abs(num),
        consumptionDate: chosenDate
      });
      setConsumptionModalBatch(null);
      setConsumptionForm({ qty: '', date: today() });
      await fetchBatches();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record consumption');
    } finally {
      setConsumingBatchId(null);
    }
  };

  const handleAddStockChange = (e) => {
    const { name, value } = e.target || e;
    setAddStockForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddStockSubmit = async (e) => {
    e.preventDefault();
    if (!addStockForm.quantity || !addStockForm.vendor_id || parseInt(addStockForm.quantity, 10) <= 0) {
      setError('Quantity and vendor are required');
      return;
    }
    try {
      setAddStockSubmitting(true);
      setError('');
      await axiosInstance.post('/materials', {
        material_name: materialName,
        purchase_date: addStockForm.purchase_date,
        quantity: parseInt(addStockForm.quantity, 10),
        rate: addStockForm.rate !== '' ? addStockForm.rate : null,
        manufacture_date: addStockForm.manufacture_date || null,
        expiry_date: addStockForm.expiry_date || null,
        vendor_id: parseInt(addStockForm.vendor_id, 10)
      });
      setShowAddStock(false);
      setAddStockForm({
        purchase_date: today(),
        quantity: '',
        rate: '',
        manufacture_date: '',
        expiry_date: '',
        vendor_id: ''
      });
      await fetchBatches();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add stock');
    } finally {
      setAddStockSubmitting(false);
    }
  };

  const handleMarkNoted = async (materialId) => {
    try {
      await axiosInstance.put(`/materials/${materialId}/noted`);
      await fetchBatches();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to mark as noted');
    }
  };

  const handleDeleteMaterial = async () => {
    try {
      setDeleting(true);
      setError('');
      await axiosInstance.put('/materials/mark-deleted', { materialName });
      setShowDeleteConfirm(false);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove material');
    } finally {
      setDeleting(false);
    }
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const getDaysToExpire = (expiryDate, asOfDate) => {
    if (!expiryDate) return '—';
    const expiry = new Date(expiryDate);
    const ref = asOfDate ? new Date(asOfDate) : new Date();
    const ms = expiry.getTime() - ref.getTime();
    const days = Math.round(ms / (24 * 60 * 60 * 1000));
    if (days > 0) return `${days} day${days !== 1 ? 's' : ''}`;
    if (days < 0) return `Expired ${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''} ago`;
    return 'Expires today';
  };

  return (
    <>
    <Modal open={true} onClose={onClose}>
      <ModalDialog
        sx={{
          maxWidth: '90vw',
          width: '1200px',
          maxHeight: '90vh',
          overflow: 'auto'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography level="h2" sx={{ color: '#eb7a24' }}>
            {materialName} - Batch Details
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="outlined"
              color="danger"
              size="sm"
              onClick={() => setShowDeleteConfirm(true)}
            >
              Remove from list
            </Button>
            <Button variant="plain" onClick={onClose}>
              Close
            </Button>
          </Box>
        </Box>

        {showDeleteConfirm && (
          <Alert
            color="neutral"
            variant="soft"
            sx={{ mb: 2 }}
            endDecorator={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="sm" variant="plain" color="neutral" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
                  Cancel
                </Button>
                <Button size="sm" variant="solid" color="danger" onClick={handleDeleteMaterial} loading={deleting}>
                  Remove
                </Button>
              </Box>
            }
          >
            Remove this material from the list? It will be marked as deleted and hidden. You can add the same material again later.
          </Alert>
        )}

        {error && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography level="h3">
              Batches
            </Typography>
            <Button
              size="sm"
              onClick={() => setShowAddStock((v) => !v)}
              sx={{
                backgroundColor: '#eb7a24',
                '&:hover': { backgroundColor: '#cc6a1f' }
              }}
            >
              {showAddStock ? 'Cancel' : 'Add stock'}
            </Button>
          </Box>

          {showAddStock && (
            <Card variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#fffbf7' }}>
              <Typography level="h4" sx={{ mb: 2, color: '#eb7a24' }}>
                Add new batch
              </Typography>
              <form onSubmit={handleAddStockSubmit}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <FormControl sx={{ minWidth: 140 }}>
                    <FormLabel>Material</FormLabel>
                    <Input value={materialName} disabled size="sm" />
                  </FormControl>
                  <FormControl sx={{ minWidth: 140 }}>
                    <FormLabel>Purchase Date *</FormLabel>
                    <Input
                      type="date"
                      name="purchase_date"
                      value={addStockForm.purchase_date}
                      onChange={handleAddStockChange}
                      size="sm"
                      required
                      sx={{ '&:focus-within': { borderColor: '#ea7a23' } }}
                    />
                  </FormControl>
                  <FormControl sx={{ width: 100 }}>
                    <FormLabel>Quantity *</FormLabel>
                    <Input
                      type="number"
                      name="quantity"
                      value={addStockForm.quantity}
                      onChange={handleAddStockChange}
                      size="sm"
                      min="1"
                      required
                      placeholder="0"
                      sx={{ '&:focus-within': { borderColor: '#ea7a23' } }}
                    />
                  </FormControl>
                  <FormControl sx={{ width: 100 }}>
                    <FormLabel>Rate (₹)</FormLabel>
                    <Input
                      type="number"
                      name="rate"
                      value={addStockForm.rate}
                      onChange={handleAddStockChange}
                      size="sm"
                      min="0"
                      step="0.01"
                      placeholder="0"
                      sx={{ '&:focus-within': { borderColor: '#ea7a23' } }}
                    />
                  </FormControl>
                  <FormControl sx={{ minWidth: 140 }}>
                    <FormLabel>Manufacturing Date</FormLabel>
                    <Input
                      type="date"
                      name="manufacture_date"
                      value={addStockForm.manufacture_date}
                      onChange={handleAddStockChange}
                      size="sm"
                      sx={{ '&:focus-within': { borderColor: '#ea7a23' } }}
                    />
                  </FormControl>
                  <FormControl sx={{ minWidth: 140 }}>
                    <FormLabel>Expiry Date</FormLabel>
                    <Input
                      type="date"
                      name="expiry_date"
                      value={addStockForm.expiry_date}
                      onChange={handleAddStockChange}
                      size="sm"
                      sx={{ '&:focus-within': { borderColor: '#ea7a23' } }}
                    />
                  </FormControl>
                  <FormControl sx={{ minWidth: 180 }}>
                    <FormLabel>Vendor *</FormLabel>
                    <Select
                      name="vendor_id"
                      value={addStockForm.vendor_id || ''}
                      onChange={(_, val) => handleAddStockChange({ target: { name: 'vendor_id', value: val } })}
                      size="sm"
                      placeholder={vendorsLoading ? 'Loading...' : 'Select vendor'}
                      disabled={vendorsLoading}
                      required
                      sx={{ minWidth: 160, '&.Joy-focused': { borderColor: '#ea7a23' } }}
                    >
                      {vendors.map((v) => (
                        <Option key={v.id} value={String(v.id)}>{v.name}</Option>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    type="submit"
                    size="sm"
                    loading={addStockSubmitting}
                    sx={{
                      alignSelf: 'flex-end',
                      backgroundColor: '#eb7a24',
                      '&:hover': { backgroundColor: '#cc6a1f' }
                    }}
                  >
                    Add stock
                  </Button>
                </Box>
              </form>
            </Card>
          )}

          <Box sx={{ overflowX: 'auto' }}>
            <Table
              sx={{
                '& thead th': {
                  backgroundColor: '#eb7a24',
                  color: 'white',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10
                },
                '& tbody tr:nth-of-type(even)': {
                  backgroundColor: '#f9f9f9'
                },
                '& tbody tr:hover': {
                  backgroundColor: '#fff4e6'
                },
                '& td': {
                  wordWrap: 'break-word',
                  whiteSpace: 'normal'
                }
              }}
            >
              <thead>
                <tr>
                  <th>Purchase Date</th>
                  <th>Days to expire</th>
                  <th>Vendor</th>
                  <th>Rate (per item)</th>
                  <th>Current Quantity</th>
                  <th>Last Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>
                      Loading...
                    </td>
                  </tr>
                ) : batches.filter((b) => Number(b.quantity) > 0).length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>
                      {batches.length === 0
                        ? 'No batches found. Use "Add stock" to add a new batch.'
                        : 'No batches with stock. Use "Add stock" to add a new batch.'}
                    </td>
                  </tr>
                ) : (
                  batches.filter((b) => Number(b.quantity) > 0).map((batch) => (
                    <tr
                      key={batch.id}
                      style={{
                        backgroundColor: isExpired(batch.expiry_date) && !batch.noted
                          ? '#ffebee'
                          : batch.noted
                          ? '#e0e0e0'
                          : 'transparent'
                      }}
                    >
                      <td>
                        {batch.purchase_date
                          ? new Date(batch.purchase_date).toLocaleDateString()
                          : 'N/A'}
                      </td>
                      <td>
                        <Typography
                          level="body-sm"
                          sx={isExpired(batch.expiry_date) && !batch.noted ? { color: 'danger', fontWeight: 500 } : {}}
                        >
                          {getDaysToExpire(batch.expiry_date)}
                        </Typography>
                      </td>
                      <td>{batch.vendor_name || 'N/A'}</td>
                      <td>{batch.rate != null ? Number(batch.rate).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'}</td>
                      <td>{batch.quantity}</td>
                      <td>
                        {batch.last_updated
                          ? new Date(batch.last_updated).toLocaleDateString()
                          : 'N/A'}
                      </td>
                      <td>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                          <Button
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            onClick={() => openConsumptionModal(batch)}
                            loading={consumingBatchId === batch.id}
                          >
                            Consumed
                          </Button>
                          {isExpired(batch.expiry_date) && !batch.noted && (
                            <Button
                              size="sm"
                              variant="soft"
                              color="warning"
                              onClick={() => handleMarkNoted(batch.id)}
                            >
                              Mark as Noted
                            </Button>
                          )}
                        </Box>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Box>
        </Card>

        <Card>
          <Typography level="h3" sx={{ mb: 2 }}>
            Change History
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table
              sx={{
                '& thead th': {
                  backgroundColor: '#eb7a24',
                  color: 'white',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10
                },
                '& tbody tr:nth-of-type(even)': {
                  backgroundColor: '#f9f9f9'
                },
                '& tbody tr:hover': {
                  backgroundColor: '#fff4e6'
                },
                '& td': {
                  wordWrap: 'break-word',
                  whiteSpace: 'normal'
                }
              }}
            >
              <thead>
                <tr>
                  <th>Consumed date</th>
                  <th>Vendor</th>
                  <th>Quantity Change</th>
                  <th>Available Quantity</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>
                      No history found
                    </td>
                  </tr>
                ) : (
                  history.map((item) => (
                    <tr key={item.id}>
                      <td>
                        {new Date(item.date).toLocaleDateString()}
                      </td>
                      <td>{item.vendor_name || 'N/A'}</td>
                      <td
                        style={{
                          color: item.change_amount > 0 ? 'green' : 'red',
                          fontWeight: 'bold'
                        }}
                      >
                        {item.change_amount > 0 ? '+' : ''}
                        {item.change_amount}
                      </td>
                      <td>{item.new_quantity}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Box>
        </Card>
      </ModalDialog>
    </Modal>

    {consumptionModalBatch && (
      <Modal open onClose={() => setConsumptionModalBatch(null)}>
        <ModalDialog sx={{ maxWidth: 400 }}>
          <Typography level="h4" sx={{ mb: 2, color: '#eb7a24' }}>
            Record consumption
          </Typography>
          <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
            Batch: {consumptionModalBatch.purchase_date
              ? new Date(consumptionModalBatch.purchase_date).toLocaleDateString()
              : 'N/A'}
            {' · '}Current qty: {consumptionModalBatch.quantity}
          </Typography>
          <form onSubmit={handleConsumptionSubmit}>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Quantity consumed *</FormLabel>
              <Input
                type="number"
                value={consumptionForm.qty}
                onChange={(e) => setConsumptionForm((prev) => ({ ...prev, qty: e.target.value }))}
                min="1"
                max={consumptionModalBatch.quantity}
                placeholder="0"
                required
                sx={{ '&:focus-within': { borderColor: '#ea7a23' } }}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Date *</FormLabel>
              <Input
                type="date"
                value={consumptionForm.date}
                max={today()}
                onChange={(e) => setConsumptionForm((prev) => ({ ...prev, date: e.target.value }))}
                required
                sx={{ '&:focus-within': { borderColor: '#ea7a23' } }}
              />
              <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.tertiary' }}>
                Cannot select a future date
              </Typography>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                type="button"
                variant="plain"
                color="neutral"
                onClick={() => setConsumptionModalBatch(null)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={consumingBatchId === consumptionModalBatch.id}
                sx={{
                  backgroundColor: '#eb7a24',
                  '&:hover': { backgroundColor: '#cc6a1f' }
                }}
              >
                Record
              </Button>
            </Box>
          </form>
        </ModalDialog>
      </Modal>
    )}
    </>
  );
};

export default MaterialPopup;

